import { useEffect, useRef, useState } from "react";
import { useCepLookup } from "../../hooks/useCepLookup";
import { CEP_ERROR_CODES } from "../../services/cep";
import "./index.css";

export const CEP_ADDRESS_FIELDS = ["street", "district", "city", "uf"];

const hasText = (value) => String(value ?? "").trim().length > 0;

export const hasExistingCepAddress = (address = {}) =>
  CEP_ADDRESS_FIELDS.some((field) => hasText(address[field]));

export const mergeCepAddressSuggestion = (currentAddress = {}, suggestion = {}) => {
  const nextAddress = { ...currentAddress };

  CEP_ADDRESS_FIELDS.forEach((field) => {
    if (suggestion[field] !== null && suggestion[field] !== undefined) {
      nextAddress[field] = suggestion[field];
    }
  });

  return nextAddress;
};

const suggestionKey = (address) =>
  address
    ? [address.zipCode, ...CEP_ADDRESS_FIELDS.map((field) => address[field] ?? "")].join("|")
    : null;

function CepAddressLookup({
  address,
  onZipCodeChange,
  onApplyAddress,
  disabled = false,
  inputClassName,
}) {
  const {
    address: suggestion,
    error,
    inputProps,
    isLoading,
    loadingMessage,
    retry,
    setCepValue,
    statusProps,
  } = useCepLookup({ initialCep: address?.zipCode });
  const handledSuggestionRef = useRef(null);
  const pendingSuggestionRef = useRef(null);
  const [pendingSuggestion, setPendingSuggestion] = useState(null);

  useEffect(() => {
    if (!suggestion) {
      handledSuggestionRef.current = null;
      pendingSuggestionRef.current = null;
      setPendingSuggestion(null);
      return;
    }

    const key = suggestionKey(suggestion);

    if (handledSuggestionRef.current === key) {
      return;
    }

    handledSuggestionRef.current = key;
    const nextAddress = mergeCepAddressSuggestion(address, suggestion);

    if (hasExistingCepAddress(address)) {
      pendingSuggestionRef.current = nextAddress;
      setPendingSuggestion(nextAddress);
      return;
    }

    onApplyAddress(nextAddress);
  }, [address, onApplyAddress, suggestion]);

  const handleCepChange = (event) => {
    const formattedCep = setCepValue(event.target.value);
    onZipCodeChange(formattedCep);
  };

  const applyPendingSuggestion = () => {
    if (!pendingSuggestionRef.current) {
      return;
    }

    onApplyAddress(pendingSuggestionRef.current);
    pendingSuggestionRef.current = null;
    setPendingSuggestion(null);
  };

  const dismissPendingSuggestion = () => {
    pendingSuggestionRef.current = null;
    setPendingSuggestion(null);
  };

  return (
    <div className="cep-address-lookup" aria-label="Consulta de CEP">
      <label className="cep-address-label">
        <span>CEP</span>
        <input
          {...inputProps}
          aria-label="CEP"
          placeholder="CEP"
          className={inputClassName}
          disabled={disabled}
          onChange={handleCepChange}
        />
      </label>

      <div className="cep-address-lookup-status" {...statusProps}>
        {isLoading ? loadingMessage : ""}
      </div>

      {error && error.code !== CEP_ERROR_CODES.UNAVAILABLE && (
        <div className="cep-address-lookup-error" role="alert">
          <span>{error.message}</span>
          {error.retryable && !disabled && (
            <button type="button" onClick={retry}>
              Buscar novamente
            </button>
          )}
        </div>
      )}

      {pendingSuggestion && (
        <div className="cep-address-lookup-confirmation" role="alert">
          <p>
            Este CEP encontrou outro endereço. Confirme antes de substituir
            logradouro, bairro, cidade e UF.
          </p>
          <div>
            <button type="button" onClick={applyPendingSuggestion} disabled={disabled}>
              Aplicar endereço sugerido
            </button>
            <button type="button" onClick={dismissPendingSuggestion} disabled={disabled}>
              Manter endereço atual
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CepAddressLookup;
